import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { SearchBar } from "../../components/vc/SearchBar";
import { EnquiryCard } from "../../components/vc/EnquiryCard";
import { StatsCard } from "../../components/vc/StatsCard";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { FloatingButton } from "../../components/vc/FloatingButton";
import { FileText, Plus, Sparkles, Clock, CheckCircle2, XCircle, ArrowUpRight, Search as SearchIcon } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";

// Map price-request statuses to enquiry statuses
const statusMap = {
  pending_quotes: "pending",
  ready: "responded",
  ordered: "closed",
};

export default function VCEnquiries() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | responded | closed
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { counts: notifCounts, list: notifList } = useNotifications();
  const enquiriesCount = notifCounts.by_section.enquiries;
  const prevEnquiriesCount = useRef(enquiriesCount);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    apiClient.get("/bulk-price-requests").then((r) => {
      setEnquiries((r.data || []).map((x) => {
        const items = x.items || [];
        const productSummary = items.slice(0, 2).map((it) => it.product).join(", ")
          + (items.length > 2 ? `, +${items.length - 2} more` : "");
        // Use backend status when present; fallback to derived
        const anyQuoted = items.some((it) => (it.quotes || []).length > 0);
        const allOrdered = items.length > 0 && items.every((it) => it.order_id);
        const status = x.status || (allOrdered ? "closed" : anyQuoted ? "responded" : "pending");

        // Response counts — number of destination businesses that have responded
        // to at least ONE item vs the total number of destination businesses.
        const supplierIds = x.supplier_ids || Object.keys(x.supplier_names || {});
        const totalSuppliers = supplierIds.length;
        const respondedIds = new Set();
        for (const it of items) {
          for (const qt of (it.quotes || [])) {
            if (qt.supplier_id) respondedIds.add(qt.supplier_id);
          }
        }
        const respondedCount = respondedIds.size;

        return {
          id: x.id,
          title: x.title || "",
          business_name: Object.values(x.supplier_names || {}).slice(0, 2).join(", ") || "Multiple businesses",
          enquiry_id_label: (x.id || "").slice(0, 6).toUpperCase(),
          date: x.created_at,
          status: status === "quoted" ? "responded" : status,
          product_summary: `${items.length} items · ${productSummary}`,
          created_at: x.created_at,
          last_response_at: x.last_response_at,
          responded_count: respondedCount,
          total_suppliers: totalSuppliers,
          items,
          supplier_ids: supplierIds,
          supplier_names: x.supplier_names || {},
        };
      }));
    }).catch(() => setEnquiries([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Automatic update — a new quote (enquiry_responded) or incoming enquiry
  // refreshes this list within ~5s with no manual reload, scroll loss, or
  // page reload. Mirrors the same pattern used on Orders/Businesses.
  useEffect(() => {
    if (enquiriesCount !== prevEnquiriesCount.current) {
      prevEnquiriesCount.current = enquiriesCount;
      load(false);
    }
  }, [enquiriesCount]);

  const incomingUnread = useMemo(
    () => notifList.filter((n) => !n.read && n.type === "enquiry_new").length,
    [notifList]
  );

  const filtered = useMemo(() => {
    let arr = enquiries;
    if (filter !== "all") arr = arr.filter((e) => e.status === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      arr = arr.filter((e) =>
        (e.title || "").toLowerCase().includes(needle) ||
        (e.product_summary || "").toLowerCase().includes(needle) ||
        (e.business_name || "").toLowerCase().includes(needle) ||
        (e.enquiry_id_label || "").toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [enquiries, filter, q]);

  const stats = useMemo(() => ({
    total: enquiries.length,
    pending: enquiries.filter((e) => e.status === "pending").length,
    responded: enquiries.filter((e) => e.status === "responded").length,
    closed: enquiries.filter((e) => e.status === "closed").length,
  }), [enquiries]);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-5">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Enquiries</h2>
          <p className="text-sm text-slate-500 mt-0.5">Ask suppliers for the best price</p>
        </div>

        {/* Hero — Create New Enquiry */}
        <button
          onClick={() => navigate("/enquiries/new")}
          data-testid="raise-enquiry-hero"
          className="w-full text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 text-white p-5 shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all group"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-4 bottom-0 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur grid place-items-center shrink-0">
              <Plus className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-90">
                <Sparkles className="w-3 h-3" /> New Enquiry
              </div>
              <div className="text-lg font-bold leading-tight mt-1">Create New Enquiry</div>
              <div className="text-xs opacity-80 mt-0.5">Excel · Image · Manual entry</div>
            </div>
            <ArrowUpRight className="w-6 h-6 opacity-80 group-hover:rotate-45 transition-transform" />
          </div>
        </button>

        {/* Incoming enquiries tile */}
        <button
          onClick={() => navigate("/enquiries/incoming")}
          data-testid="incoming-enquiries-tile"
          className="w-full text-left flex items-center gap-3 bg-white border border-slate-100 rounded-2xl p-3.5 hover:shadow-md hover:border-slate-200 transition"
        >
          <div className="relative w-11 h-11 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
            <FileText className="w-5 h-5" strokeWidth={2.2} />
            {incomingUnread > 0 && (
              <span
                data-testid="incoming-enquiries-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold grid place-items-center leading-none"
              >
                {incomingUnread > 9 ? "9+" : incomingUnread}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-slate-900 text-sm">Incoming enquiries</div>
            <div className="text-xs text-slate-500">Send quotations to buyers</div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-300" />
        </button>

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2" data-testid="enquiry-stats">
          <StatsCard label="Total" value={stats.total} tone="slate" Icon={FileText} testid="stat-total" />
          <StatsCard label="Pending" value={stats.pending} tone="amber" Icon={Clock} testid="stat-pending" />
          <StatsCard label="Quoted" value={stats.responded} tone="emerald" Icon={CheckCircle2} testid="stat-responded" />
          <StatsCard label="Closed" value={stats.closed} tone="slate" Icon={XCircle} testid="stat-closed" />
        </div>

        {/* Search */}
        <SearchBar value={q} onChange={setQ} placeholder="Search enquiries" testid="enquiries-search" />

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1" data-testid="enquiries-filters">
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["responded", "Quoted"],
            ["closed", "Closed"],
          ].map(([v, l]) => (
            <button
              key={v}
              data-testid={`enq-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-bold transition ${
                filter === v
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Enquiry list */}
        <Section title="Outgoing Enquiries" subtitle={`${filtered.length} shown`} testid="enquiries-list-section">
          {loading ? <SkeletonList /> : filtered.length === 0 ? (
            <EmptyState
              Icon={FileText}
              title={q || filter !== "all" ? "No matches" : "No enquiries yet"}
              subtitle={q || filter !== "all" ? "Try changing the search or filter" : "Raise your first enquiry to compare supplier prices"}
            />
          ) : (
            <div className="space-y-2">
              {filtered.map((e) => (
                <EnquiryCard key={e.id} enquiry={e} to={`/enquiries/${e.id}`} />
              ))}
            </div>
          )}
        </Section>
      </div>

      <FloatingButton
        Icon={SearchIcon}
        onClick={() => document.querySelector('[data-testid="enquiries-search"]')?.focus()}
        testid="enquiries-fab"
      />
    </>
  );
}
